
import React from 'react';
import { logError } from '../services/telemetryService.ts';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const LOCAL_STORAGE_KEY = 'devcore_snapshot';

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logError(error, { componentStack: errorInfo.componentStack });
  }
  
  handleRevert = () => {
    // We simply reload the page. The GlobalStateProvider will load from the last snapshot.
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center p-8 text-center bg-red-900/20 text-red-300">
            <h1 className="text-3xl font-bold text-red-200 mb-2">Something went wrong.</h1>
            <p className="text-lg mb-4 max-w-md">A feature has crashed. You can try reverting to the last saved checkpoint.</p>
            <details className="text-left bg-slate-900/50 p-2 rounded-md max-w-xl text-xs font-mono mb-4">
              <summary>Error Details</summary>
              <pre className="mt-2 whitespace-pre-wrap">{this.state.error?.stack}</pre>
            </details>
            <button
                onClick={this.handleRevert}
                className="px-6 py-2 bg-yellow-500 text-slate-900 font-bold rounded-md hover:bg-yellow-400"
            >
                Revert to last checkpoint
            </button>
        </div>
      );
    }

    return this.props.children;
  }
}
